/**
 * Browser-level TTF / OpenType font loader.
 *
 * Wraps the `FontFace` Web API so the rest of the fonts layer can treat
 * TTF bytes symmetrically with parsed SHX fonts. A successful load:
 *
 *   1. constructs a `FontFace` from the bytes,
 *   2. awaits the browser's internal parse (`.load()`),
 *   3. registers the face with `document.fonts` so every `<text>` /
 *      `<canvas>` render picks it up via normal CSS `font-family`
 *      resolution.
 *
 * The raw `bytes` are preserved on the returned `LoadedTtf` so the
 * host drawing can re-serialise the font into an exported DXF / ZIP
 * without having to re-fetch from the `FontStore`. This is the same
 * contract `ShxFont` provides for SHX fonts (the parser keeps the
 * bytes implicitly via its glyph table).
 *
 * ### Why not `opentype.js`?
 *
 * AutoCAD-parity TTF rendering for TEXT / MTEXT only needs the glyph
 * metrics the browser already exposes through layout + `getBBox`. The
 * renderer asks `document.fonts` whether a family is "loaded" and
 * emits SVG `<text font-family="…">` — no glyph introspection needed.
 * Pulling in `opentype.js` (~200 KB gzipped) just to re-parse what the
 * browser already parses would be a regression.
 *
 * ### Why synthetic family names?
 *
 * DXF files reference fonts by `TTF typeface` (group 1000) or by the
 * STYLE font name. When the drawing is loaded before the font is (or
 * when the user uploads a `.ttf` directly), we still need a stable CSS
 * family to bind renderer state to. The synthetic name — derived from
 * a 32-byte rolling hash of the file — is deterministic per-file so
 * the same upload always resolves to the same `FontFace` even across
 * browser sessions. Callers who know the "real" family (e.g. via the
 * filename or a manifest) can pass `explicitFamily` to override.
 */

/**
 * Result of a successful `loadTtfFromBytes` call. `family` is the CSS
 * `font-family` token consumers should emit into `<text>` elements.
 * `fontFace` is already registered with `document.fonts` — call
 * `unloadTtf` to revoke it.
 */
export type LoadedTtf = {
  /** CSS `font-family` token. Case-sensitive per the CSSOM spec. */
  family: string;
  /** Raw TTF / OTF bytes; preserved so the drawing can re-export. */
  bytes: Uint8Array;
  /** The `FontFace` instance that's currently in `document.fonts`. */
  fontFace: FontFace;
};

/**
 * Discriminated result so callers can surface a specific message per
 * failure mode:
 *
 *   - `load-failed`: bytes were supplied but the browser refused to
 *     parse them (corrupt file, unsupported sfnt variant, …). Users
 *     see a toast with the underlying `message`.
 *   - `family-conflict`: reserved for future use when we'll de-dupe
 *     explicit families that collide with a different set of bytes.
 *     Currently never returned — collision handling is left to
 *     `FontRegistry.register`.
 *   - `api-unavailable`: the runtime has no `FontFace` constructor
 *     (jsdom without shim, legacy browser). The app falls back to
 *     vector glyph rendering via SHX and the UI shows a warning.
 */
export type TtfLoadResult =
  | { ok: true; ttf: LoadedTtf }
  | {
      ok: false;
      code: "load-failed" | "family-conflict" | "api-unavailable";
      message: string;
    };

/**
 * Load TTF bytes and register the resulting `FontFace` with
 * `document.fonts`. Never throws — every failure mode is encoded in
 * the returned discriminated result.
 *
 * When `explicitFamily` is omitted or blank, a deterministic synthetic
 * name is derived from a rolling hash of the first 32 bytes of the
 * file. See module header for rationale.
 */
export async function loadTtfFromBytes(
  bytes: Uint8Array,
  explicitFamily?: string
): Promise<TtfLoadResult> {
  if (typeof FontFace === "undefined") {
    return {
      ok: false,
      code: "api-unavailable",
      message: "FontFace API not supported in this runtime",
    };
  }

  const trimmed = explicitFamily?.trim() ?? "";
  const family = trimmed !== "" ? trimmed : deriveFamilyFromBytes(bytes);

  // FontFace accepts `BufferSource`; passing the Uint8Array's backing
  // buffer directly avoids a redundant copy. We slice in case the
  // Uint8Array is a view over a larger ArrayBuffer (e.g. when loaded
  // via `slice(offset, offset + len)` from a container).
  // `.buffer.slice(...)` always returns a plain ArrayBuffer (never
  // SharedArrayBuffer), so the cast is safe and matches FontFace typings.
  const source = bytes.buffer.slice(
    bytes.byteOffset,
    bytes.byteOffset + bytes.byteLength
  ) as ArrayBuffer;
  const fontFace = new FontFace(family, source);

  try {
    await fontFace.load();
  } catch (error) {
    return {
      ok: false,
      code: "load-failed",
      message: error instanceof Error ? error.message : String(error),
    };
  }

  try {
    document.fonts.add(fontFace);
  } catch (error) {
    // `document.fonts.add` only throws if the face was constructed
    // with a CSSFontFaceRule and we're trying to double-register —
    // neither applies here. Surface it as `load-failed` for safety.
    return {
      ok: false,
      code: "load-failed",
      message: error instanceof Error ? error.message : String(error),
    };
  }

  return { ok: true, ttf: { family, bytes, fontFace } };
}

/**
 * Revoke a previously loaded TTF. After this call the browser will no
 * longer resolve the CSS family in `document.fonts` and subsequent
 * `<text font-family="…">` renders will fall back to the next face in
 * the CSS family stack.
 *
 * No-op if the face has already been removed — `document.fonts.delete`
 * itself tolerates that, so we do not guard here.
 */
export function unloadTtf(ttf: LoadedTtf): void {
  if (typeof document === "undefined" || !document.fonts) return;
  document.fonts.delete(ttf.fontFace);
}

/**
 * Derive a deterministic synthetic CSS family name from the first 32
 * bytes of the file. The hash is a 32-bit `h = (h << 5) - h + byte`
 * rolling accumulator (djb2-style) that's cheap enough to run inline
 * on every upload. 32 bytes is plenty to disambiguate real TTF files
 * since the sfnt header + `numTables` + first table record already
 * varies across fonts.
 *
 * The resulting name is prefixed with `ImportedTTF-` so the
 * `FontManagerDialog` can recognise synthetic vs explicit bindings.
 */
function deriveFamilyFromBytes(bytes: Uint8Array): string {
  let hash = 0;
  const limit = Math.min(bytes.length, 32);
  for (let i = 0; i < limit; i++) {
    // `| 0` forces int32 arithmetic so the hash stays 32-bit on every
    // iteration (otherwise it would drift into float territory at
    // ~2^53). Matches the v8-style `Math.imul` pattern without the
    // extra function call.
    hash = ((hash << 5) - hash + (bytes[i] ?? 0)) | 0;
  }
  const hex = (hash >>> 0).toString(16).padStart(8, "0");
  return `ImportedTTF-${hex}`;
}

/**
 * Best-effort sniff of a binary font buffer.
 *
 * SHX detection: the first 24 ASCII bytes of every supported SHX
 * fixture start with `AutoCAD-` followed by the `unifont` keyword.
 * Version-specific suffixes (`86 unifont 1.0`, `2000 unifont 2.0`, …)
 * all satisfy that pattern — we match loosely so a future SHX revision
 * still routes into the SHX parser rather than failing fast.
 *
 * TTF / OTF detection uses the sfnt version field at offset 0:
 *   - `00 01 00 00`  TrueType (most common)
 *   - `OTTO`         OpenType with CFF outlines
 *   - `true`         legacy Mac TrueType
 *   - `typ1`         PostScript Type 1 wrapped in sfnt
 *
 * Anything else falls through to `"unknown"` — callers treat that as a
 * user-visible parse error. Buffers shorter than 8 bytes are rejected
 * outright since neither format has a valid header in that range.
 */
export function detectFontKind(bytes: Uint8Array): "shx" | "ttf" | "unknown" {
  if (bytes.length < 8) return "unknown";

  // SHX magic: `AutoCAD-<version> unifont <fmt-version>`. We scan the
  // first 24 bytes as ASCII; non-ASCII values come back as raw byte
  // codepoints from String.fromCharCode which is fine — we only look
  // for the two literal substrings.
  const asciiHead = String.fromCharCode(...bytes.slice(0, 24));
  if (asciiHead.startsWith("AutoCAD-") && asciiHead.includes("unifont")) {
    return "shx";
  }

  const b0 = bytes[0];
  const b1 = bytes[1];
  const b2 = bytes[2];
  const b3 = bytes[3];

  // TrueType: 0x00010000
  if (b0 === 0x00 && b1 === 0x01 && b2 === 0x00 && b3 === 0x00) return "ttf";
  // OpenType / CFF: 'OTTO'
  if (b0 === 0x4f && b1 === 0x54 && b2 === 0x54 && b3 === 0x4f) return "ttf";
  // Legacy Mac TrueType: 'true'
  if (b0 === 0x74 && b1 === 0x72 && b2 === 0x75 && b3 === 0x65) return "ttf";
  // PostScript Type 1 in sfnt: 'typ1'
  if (b0 === 0x74 && b1 === 0x79 && b2 === 0x70 && b3 === 0x31) return "ttf";

  return "unknown";
}
